import dataService from "../../api";
import { startLoading, stopLoading, serverErrorMessage, showNotification, showAlert, catchHandler } from "../../utils/helpers";

const state = {
    schedules: [],
    assignments: [],
    totalAssignments: 0,
    periods: [],
    reminders: [],
};

const mutations = {
    setSchedules: (state, r) => (state.schedules = r),
    setAssignments: (state, r) => (state.assignments = r),
    setTotalAssignments: (state, r) => (state.totalAssignments = r),
    setPeriods: (state, r) => (state.periods = r),
    setReminders: (state, r) => (state.reminders = r),
};

const buildParams = params =>
    Object.entries(params)
        .filter(([, val]) => val !== null && val !== undefined && val !== "")
        .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
        .join("&");

// Every call follows the same shape as the other modules: loader, statusCode check,
// success snackbar for writes, catchHandler for errors (it shows the server message).
async function call(request, { notify = false, quiet = false } = {}) {
    try {
        if (!quiet) startLoading();
        const response = await request();
        if (!quiet) stopLoading();

        if (!response.data.statusCode) {
            showAlert(serverErrorMessage);
            return Promise.reject(new Error("statusCode"));
        }

        if (notify) showNotification(response.data.message);
        return response.data;
    } catch (error) {
        if (!quiet) stopLoading();
        if (error.message !== "statusCode") catchHandler(error);
        return Promise.reject(error);
    }
}

const actions = {
    // ── Schedules ─────────────────────────────────────────────
    async getSchedules({ commit }) {
        const data = await call(() => dataService.workSchedules.getSchedules()).catch(() => null);
        if (data) commit("setSchedules", data.schedules);
    },
    getSchedule: (_, id) => call(() => dataService.workSchedules.getSchedule(id)).then(d => d.schedule),
    previewSchedule: (_, { id, params }) =>
        call(() => dataService.workSchedules.previewSchedule(id, buildParams(params))).then(d => d.days),
    addSchedule: (_, payload) => call(() => dataService.workSchedules.addSchedule(payload), { notify: true }),
    editSchedule: (_, { id, payload }) => call(() => dataService.workSchedules.editSchedule(id, payload), { notify: true }),
    makeDefault: (_, id) => call(() => dataService.workSchedules.makeDefault(id), { notify: true }),
    deleteSchedule: (_, id) => call(() => dataService.workSchedules.deleteSchedule(id), { notify: true }),

    // ── Assignments ───────────────────────────────────────────
    async getAssignments({ commit }, params) {
        const data = await call(() => dataService.workSchedules.getAssignments(buildParams(params || {}))).catch(() => null);
        if (data) {
            commit("setAssignments", data.assignments);
            commit("setTotalAssignments", data.totalItems);
        }
    },
    getAssignment: (_, id) => call(() => dataService.workSchedules.getAssignment(id)).then(d => d.assignment),
    addAssignment: (_, payload) => call(() => dataService.workSchedules.addAssignment(payload), { notify: true }),
    editAssignment: (_, { id, payload }) => call(() => dataService.workSchedules.editAssignment(id, payload), { notify: true }),
    deleteAssignment: (_, id) => call(() => dataService.workSchedules.deleteAssignment(id), { notify: true }),
    getEffective: (_, params) =>
        call(() => dataService.workSchedules.getEffective(buildParams(params))).then(d => d.days),

    // ── Special periods ───────────────────────────────────────
    async getPeriods({ commit }) {
        const data = await call(() => dataService.workSchedules.getPeriods()).catch(() => null);
        if (data) commit("setPeriods", data.periods);
    },
    getPeriod: (_, id) => call(() => dataService.workSchedules.getPeriod(id)).then(d => d.period),
    addPeriod: (_, payload) => call(() => dataService.workSchedules.addPeriod(payload), { notify: true }),
    editPeriod: (_, { id, payload }) => call(() => dataService.workSchedules.editPeriod(id, payload), { notify: true }),
    deletePeriod: (_, id) => call(() => dataService.workSchedules.deletePeriod(id), { notify: true }),
    getPeriodReview: (_, id) => call(() => dataService.workSchedules.getPeriodReview(id)).then(d => d.employees),

    // Shown on the dashboard: no loader, and no error popup if it fails.
    async getReminders({ commit }) {
        try {
            const response = await dataService.workSchedules.getReminders();
            commit("setReminders", response.data.statusCode ? response.data.reminders : []);
        } catch (error) {
            commit("setReminders", []);
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};
